import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ImageViewerWidget from '@/widgets/UploadFilesWidgets/ImageViewerWidget';
import SecondStepFileAddFiles from '@/widgets/UploadFilesWidgets/SecondStepFileAddFiles';
import SecondStepFileUpload from '@/widgets/UploadFilesWidgets/SecondStepFileUpload';
import UploadFilesTable from '@/widgets/UploadFilesWidgets/UploadFilesTable';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { AgreementDTO, FileUploadDTO, FileUrlDTO } from '@/types/api-types';
import { NormalizedFile } from '@/types/file-explorer-types';

import { getFileErrorType, mapFileToDTO } from '@/lib/utils';

import { usePrompt } from '@/hooks/usePrompt';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';
import FilterBadgesUploadComponent from '@/components/FileExplorer/FilterBadgesUploadComponent';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useAuthStore } from '@/stores/authStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useFileUploadStore } from '@/stores/fileUploadStore';
import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';
import { FileUploadService } from '@/services/file-upload-service';

import { createThumbnail } from '@/utils/thumbnail-creator';
import { createWebpVersion } from '@/utils/webp-creator';

import { FILE_UPLOAD_STATUS } from '@/constants/global-constants';
import { executorRoutes } from '@/constants/route-settings';

export const UploadFilesPage = () => {
  const { '*': agreementUuid } = useParams();
  const navigate = useNavigate();
  const [deleteFileId, setDeleteFileId] = useState('');
  const [viewFile, setViewFile] = useState<FileUploadDTO | null>(null);
  const [viewErrorFileId, setViewErrorFileId] = useState('');
  const { getFileLinks } = useFileUploadStore();
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [ignoreDateErrors, setIgnoreDateErrors] = useState(false);
  const { fileValidationSettings } = useGlobalStore();

  usePrompt(hasUnsavedChanges);

  const currentAgreementUuid = useMemo(
    () => agreementUuid || uuidv4(),
    [agreementUuid],
  );

  const { getFilesState, setFilesState } = useFileExplorerStore();

  const { createFile, deleteFile, setAgreements } = useApiManagerStore();

  const [agreementUuidForRedirect, setAgreementUuidForRedirect] = useState<
    string | null
  >(null);

  const { user } = useAuthStore();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [filesToUpload, setFilesToUpload] = useState<FileUploadDTO[]>([]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    const newPreselectedFiles = await Promise.all(
      selectedFiles.map((file) =>
        mapFileToDTO(
          file,
          currentAgreementUuid,
          fileValidationSettings[0]?.max_upload_date_days || 14,
          user?.uuid || '',
          fileValidationSettings[0]
            ? {
                check_metadata: fileValidationSettings[0].check_metadata,
                min_resolution:
                  fileValidationSettings[0].min_resolution || undefined,
                max_resolution:
                  fileValidationSettings[0].max_resolution || undefined,
                max_shooting_date_days:
                  fileValidationSettings[0].max_shooting_date_days || undefined,
                allowed_formats:
                  fileValidationSettings[0].allowed_formats || undefined,
              }
            : undefined,
        ),
      ),
    );

    setFilesToUpload((prev) => [...prev, ...newPreselectedFiles]);
    setHasUnsavedChanges(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveUpload = useCallback((file: FileUploadDTO) => {
    if (file.status === FILE_UPLOAD_STATUS.VALIDATED) {
      setDeleteFileId(file.uuid);
      return;
    }
    setFilesToUpload((prev) => prev.filter((f) => f.name !== file.name));
  }, []);

  const handleUploadFiles = async (agreementUuid: string, prePath: string) => {
    const filesState = await getFilesState(agreementUuid);
    const newCommentsArray: string[] = [];
    setAgreementUuidForRedirect(agreementUuid);

    setIsUploading(true);
    const filesToUploadArrayCollected = filesToUpload
      .filter((file) => {
        // Include PENDING files
        if (file.status === FILE_UPLOAD_STATUS.PENDING) return true;
        // Include files with date errors when ignoreDateErrors is checked
        if (
          ignoreDateErrors &&
          file.status === FILE_UPLOAD_STATUS.UPLOAD_FAILED &&
          file.error &&
          getFileErrorType(file.error) === 'date'
        ) {
          return true;
        }
        return false;
      })
      .map(
        (file) =>
          ({
            ...file,
            name: file.name.toLowerCase(),
            agreement: agreementUuid,
          }) as FileUploadDTO,
      );

    const filesToUploadArrayCollectedToGetFileLink =
      filesToUploadArrayCollected.map(
        (file) => `${prePath}/${encodeURIComponent(file.name)}`,
      );

    const fileUrlDTO: FileUrlDTO = {
      agreement_uuid: agreementUuid,
      file_name_paths: filesToUploadArrayCollectedToGetFileLink,
    };

    const fileUrlsResponse = await getFileLinks(fileUrlDTO);

    setFilesToUpload((prev) =>
      prev.map((f) => ({
        ...f,
        status: FILE_UPLOAD_STATUS.UPLOADING,
      })),
    );

    const filesToUploadArrayCollectedWithFileLink =
      filesToUploadArrayCollected.map((file) => ({
        ...file,
        file_link:
          fileUrlsResponse[`${prePath}/${encodeURIComponent(file.name)}`]
            .base_url,
        write_url:
          fileUrlsResponse[`${prePath}/${encodeURIComponent(file.name)}`]
            .write_url,
        blob_name:
          fileUrlsResponse[`${prePath}/${encodeURIComponent(file.name)}`]
            .blob_name,
        thumbnail_url:
          fileUrlsResponse[`${prePath}/${encodeURIComponent(file.name)}`]
            .thumbnail_url,
        webp_url:
          fileUrlsResponse[`${prePath}/${encodeURIComponent(file.name)}`]
            ?.webp_url || null,
      }));

    try {
      const uploadPromises = filesToUploadArrayCollectedWithFileLink.map(
        async (file) => {
          try {
            // Upload file with progress tracking
            const newFileUploadService = new FileUploadService();

            // First create thumbnail and WebP version for images
            let thumbnailUrl: string | null = null;
            let webpUrl: string | null = null;
            
            if (file.file_data?.type.startsWith('image/')) {
              // Create and upload thumbnail
              try {
                const thumbnail = await createThumbnail(file.file_data!);

                if (thumbnail && file.thumbnail_url) {
                  await newFileUploadService.uploadThumbnail(
                    thumbnail,
                    file.thumbnail_url,
                  );
                  thumbnailUrl = file.thumbnail_url;
                }
              } catch (error) {
                console.error('Thumbnail creation failed:', error);
              }

              // Create and upload WebP version
              try {
                const webpData = await createWebpVersion(file.file_data!);

                if (webpData && file.webp_url) {
                  await newFileUploadService.uploadWebp(
                    webpData,
                    file.webp_url,
                  );
                  // Extract the base URL from the write URL for storage
                  webpUrl = file.webp_url.split('?')[0];
                }
              } catch (error) {
                console.error('WebP conversion failed:', error);
                // WebP conversion failure should not block upload
              }
            }

            await newFileUploadService.uploadFile(file, (progress) => {
              setFilesToUpload((prev) =>
                prev.map((f) => {
                  if (f.uuid !== file.uuid) return f;
                  const rawPercent = Math.floor(
                    (progress.uploaded / progress.total) * 100,
                  );
                  const percent =
                    f.status === FILE_UPLOAD_STATUS.VALIDATED
                      ? 100
                      : Math.min(rawPercent, 99); // do not show 100% until VALIDATED
                  return {
                    ...f,
                    progress: percent,
                    thumbnail_url: thumbnailUrl,
                    webp_url: webpUrl,
                  };
                }),
              );
            });

            // Create file record after upload
            const { uuid, ...fileWithoutUuid } = file;
            
            // Collect date-related errors if uploading with ignoreDateErrors
            const dateErrors = (file.errors || []).filter(
              (e) => getFileErrorType(e) === 'date',
            );
            
            const newFile = await createFile({
              ...fileWithoutUuid,
              status: FILE_UPLOAD_STATUS.VALIDATED,
              validation_errors: ignoreDateErrors ? dateErrors : [],
            });

            const comment = {
              content: `Файл ${file.name} був завантажений`,
              agreement: agreementUuid,
              user: user?.uuid!,
              is_edited: false,
              created: new Date().toISOString(),
              modified: new Date().toISOString(),
              is_system_comment: true,
            };
            const newComment = await apiService.createComment(comment);

            newCommentsArray.push(newComment.uuid);

            setFilesToUpload((prev) =>
              prev.map((f) =>
                f.uuid === uuid
                  ? {
                      ...f,
                      uuid: newFile.uuid,
                      status: FILE_UPLOAD_STATUS.VALIDATED,
                      progress: 100,
                    }
                  : f,
              ),
            );

            return newFile;
          } catch (error) {
            // Handle error for individual file
            setFilesToUpload((prev) =>
              prev.map((f) =>
                f.uuid === file.uuid
                  ? {
                      ...f,
                      status: FILE_UPLOAD_STATUS.UPLOAD_FAILED,
                    }
                  : f,
              ),
            );
          }
        },
      );

      // Wait for all uploads to complete
      const result = await Promise.all(uploadPromises);

      // Check if any files were uploaded with validation errors
      const hasFilesWithErrors = result.some(
        (f) => f && f.validation_errors && f.validation_errors.length > 0,
      );

      const agreements = useApiManagerStore.getState().agreements;
      const foundAgreement = agreements.find((a) => a.uuid === agreementUuid)!;

      // Update agreement with has_file_errors flag if files with errors were uploaded
      const updatedAgreement: AgreementDTO = {
        ...foundAgreement,
        comments: [...(foundAgreement.comments || []), ...newCommentsArray],
        has_file_errors: foundAgreement.has_file_errors || hasFilesWithErrors,
      };

      // Update agreement in backend if it has file errors
      if (hasFilesWithErrors && !foundAgreement.has_file_errors) {
        await apiService.updateAgreement(agreementUuid, updatedAgreement);
      }

      setAgreements([
        updatedAgreement,
        ...agreements.filter((a) => a.uuid !== agreementUuid),
      ]);

      setHasUnsavedChanges(false);

      // Convert uploaded files to NormalizedFile format
      const uploadedFiles = result
        .filter((f) => f && f.status === FILE_UPLOAD_STATUS.VALIDATED)
        .reduce(
          (acc, file) => {
            if (file) {
              acc[file.uuid] = {
                ...file,
                breadcrumb_name: file.name,
                parentId: file.agreement,
              } as NormalizedFile;
            }
            return acc;
          },
          {} as Record<string, NormalizedFile>,
        );

      setFilesState({
        ...filesState,
        entities: {
          ...filesState.entities,
          ...uploadedFiles,
        },
        rootIds: Array.from(
          new Set([...filesState.rootIds, ...Object.keys(uploadedFiles)]),
        ),
      });
      const successCount = result.filter(Boolean).length;
      if (successCount > 0) {
        setIsUploadSuccess(true);
      }
    } catch (error) {
      // TODO
      // setFilesToUpload((prev) =>
      //   prev.map((file) =>
      //     file.uuid === file.uuid
      //       ? { ...file, status: FILE_UPLOAD_STATUS.UPLOAD_FAILED }
      //       : file,
      //   ),
      // );
    } finally {
      setIsUploading(false);
      setHasUnsavedChanges(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    setDeleteFileId('');
    await deleteFile(fileId);
  };

  const handleViewFile = useCallback((file: FileUploadDTO) => {
    setViewFile(file);
  }, []);

  const handleViewError = useCallback((file: FileUploadDTO) => {
    setViewErrorFileId(file.error!);
  }, []);

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // This clears the selected files
    }
  };

  const filesToUploadLength = useMemo(() => {
    return filesToUpload.filter((file) => {
      if (file.status === FILE_UPLOAD_STATUS.VALIDATED) return false;
      if (file.status === FILE_UPLOAD_STATUS.UPLOAD_FAILED) {
        // Count date-error files as uploadable when ignoreDateErrors is checked
        return (
          ignoreDateErrors &&
          file.error &&
          getFileErrorType(file.error) === 'date'
        );
      }
      return true;
    }).length;
  }, [filesToUpload, ignoreDateErrors]);

  const { hasInvalidFiles, hasOnlyDateErrors } = useMemo(() => {
    const filesWithErrors = filesToUpload.filter(
      (file) => file.status === FILE_UPLOAD_STATUS.UPLOAD_FAILED,
    );

    const hasOnlyDateErrors =
      filesWithErrors.length > 0 &&
      filesWithErrors.every(
        (file) => file.error && getFileErrorType(file.error) === 'date',
      );

    return {
      hasInvalidFiles: filesWithErrors.length > 0,
      hasOnlyDateErrors,
    };
  }, [filesToUpload]);

  const [hasCurrentAgreement, setHasCurrentAgreement] = useState(false);

  const { overallProgressPercent, completedCount, plannedCount } =
    useMemo(() => {
      const tracked = filesToUpload.filter(
        (f) =>
          f.status === FILE_UPLOAD_STATUS.UPLOADING ||
          f.status === FILE_UPLOAD_STATUS.VALIDATED,
      );
      const planned = tracked.length;
      const completed = tracked.filter(
        (f) => f.status === FILE_UPLOAD_STATUS.VALIDATED,
      ).length;
      // Overall progress should reflect only fully completed files
      const percent = planned ? Math.round((completed / planned) * 100) : 0;
      return {
        overallProgressPercent: percent,
        completedCount: completed,
        plannedCount: planned,
      };
    }, [filesToUpload]);

  useEffect(() => {
    if (agreementUuid) {
      setHasCurrentAgreement(true);
      getFilesState(agreementUuid);
    }
  }, [agreementUuid]);

  return (
    <div className="flex h-full w-full flex-col">
      {!!viewFile && (
        <ImageViewerWidget
          isPhotoViewerOpen={!!viewFile}
          setIsPhotoViewerOpen={() => setViewFile(null)}
          imageFile={viewFile.file_data}
        />
      )}
      <ConfirmationDialog
        isOpen={!!deleteFileId}
        onClose={() => setDeleteFileId('')}
        onConfirm={() => handleDeleteFile(deleteFileId)}
        title="Ви впевнені, що хочете видалити файл?"
        confirmText="Видалити"
        cancelText="Скасувати"
        variant="destructive"
      />
      <Dialog
        open={!!viewErrorFileId}
        onOpenChange={() => setViewErrorFileId('')}
      >
        <DialogContent className="w-full max-w-[400px] p-5 pb-7">
          <DialogHeader>
            <DialogTitle className="mb-6 text-2xl font-semibold">
              Помилка
            </DialogTitle>
            <DialogDescription className="text-base text-primary">
              {viewErrorFileId}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={isUploading} onOpenChange={() => {}}>
        <DialogContent
          hideClose
          className="w-full max-w-[560px] rounded-2xl p-8 shadow-2xl"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-primary">
              Завантаження даних
            </DialogTitle>
            <DialogDescription className="text-base text-primary" hidden>
              Будь ласка, зачекайте. Триває завантаження вибраних файлів.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <Progress
              value={overallProgressPercent}
              className="h-[56px] rounded-[16px] border border-neutral-200 bg-neutral-100"
              indicatorClassName="bg-[#87FF78]"
            />
            <div className="flex items-center justify-between text-sm text-neutral-500">
              <span className="text-base font-medium text-primary">
                {overallProgressPercent}%
              </span>
              <span className="text-base font-medium text-primary">
                {completedCount}/{plannedCount}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isUploadSuccess}
        onOpenChange={(open) => {
          if (!open) {
            setIsUploadSuccess(false);
            navigate(
              `${executorRoutes.myDocuments}/?t=file&id=${agreementUuidForRedirect}`,
            );
          }
        }}
      >
        <DialogContent className="w-full max-w-[560px] rounded-2xl p-8 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-primary">
              Дані завантажено
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              className="max-w-[200px] px-6 text-base"
              onClick={() => {
                setIsUploadSuccess(false);
                navigate(
                  `${executorRoutes.myDocuments}/?t=file&id=${agreementUuidForRedirect}`,
                );
              }}
            >
              Папка з файлами
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex h-full flex-wrap gap-5 px-3 py-5 sm:gap-[50px] sm:px-[50px] sm:py-[50px]">
        <div className="flex h-full flex-1 flex-col overflow-hidden rounded-md bg-white shadow-sm sm:w-2/3">
          <div className="flex flex-shrink-0 items-center justify-between border-b p-6 pl-9">
            <p className="text-2xl font-medium">Крок №1: Додати файл(и)</p>
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept={fileValidationSettings[0]?.allowed_formats
                ?.filter((format) => format.enabled)
                ?.map((format) => `.${format.id}`)
                .join(',')}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div className="flex items-start gap-2">
              <FilterBadgesUploadComponent />
              <Button
                variant="outline"
                className="h-[40px] w-[150px]"
                onClick={() => {
                  clearFileInput();
                  fileInputRef.current?.click();
                }}
              >
                Додати файли
              </Button>
            </div>
          </div>
          <div className="custom-scrollbar flex-grow overflow-auto">
            <UploadFilesTable
              files={filesToUpload}
              setFilesToUpload={setFilesToUpload}
              isLoading={false}
              onRemove={handleRemoveUpload}
              onView={handleViewFile}
              onViewError={handleViewError}
            />
          </div>
        </div>

        {hasCurrentAgreement ? (
          <SecondStepFileAddFiles
            filesToUploadLength={filesToUploadLength}
            handleUploadFiles={handleUploadFiles}
            currentAgreementUuid={currentAgreementUuid}
            isUploading={isUploading}
            hasInvalidFiles={hasInvalidFiles}
            hasOnlyDateErrors={hasOnlyDateErrors}
            ignoreDateErrors={ignoreDateErrors}
            setIgnoreDateErrors={setIgnoreDateErrors}
          />
        ) : (
          <SecondStepFileUpload
            filesToUploadLength={filesToUploadLength}
            handleUploadFiles={handleUploadFiles}
            isUploading={isUploading}
            hasInvalidFiles={hasInvalidFiles}
            hasOnlyDateErrors={hasOnlyDateErrors}
            ignoreDateErrors={ignoreDateErrors}
            setIgnoreDateErrors={setIgnoreDateErrors}
          />
        )}
      </div>
    </div>
  );
};
